import { useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { getDirectory, findMentionFragment } from "../../../mentions";
import { appState } from "../../../state";

interface MentionInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  className?: string;
  autoFocus?: boolean;
}

// React port of mentions.ts's "Inline @mention autocomplete" section - the
// original was wired via document-level delegation against the vanilla
// comment form's specific ids/classes; this reimplements the same
// matching/positioning logic as a reusable controlled component instead,
// used for the composer, reply forms, and edit forms alike. Reuses the
// existing .mention-autocomplete/.mention-autocomplete-item CSS classes
// directly for pixel parity, portaled to document.body like the original
// (so it isn't clipped by any overflow:hidden ancestor).
export function MentionInput({
  value,
  onChange,
  placeholder,
  multiline,
  rows,
  maxLength,
  className,
  autoFocus,
}: MentionInputProps) {
  const [matches, setMatches] = useState<string[]>([]);
  const [dropdownPos, setDropdownPos] = useState<{ left: number; top: number; minWidth: number } | null>(null);
  const elRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const fragmentRef = useRef<{ start: number; query: string } | null>(null);

  function updateMatches(el: HTMLInputElement | HTMLTextAreaElement) {
    const caret = el.selectionStart ?? el.value.length;
    const frag = findMentionFragment(el.value, caret);
    fragmentRef.current = frag;

    if (!frag) {
      setMatches([]);
      setDropdownPos(null);
      return;
    }

    const q = frag.query.toLowerCase();
    const found = getDirectory()
      .filter((u) => u.fullName && u.email !== appState.currentUserEmail && u.fullName.toLowerCase().startsWith(q))
      .slice(0, 5)
      .map((u) => u.fullName);

    if (!found.length) {
      setMatches([]);
      setDropdownPos(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    setDropdownPos({
      left: window.scrollX + rect.left,
      top: window.scrollY + rect.bottom + 4,
      minWidth: Math.min(Math.max(rect.width, 160), 240),
    });
    setMatches(found);
  }

  function applyMention(fullName: string) {
    const el = elRef.current;
    const frag = fragmentRef.current;
    if (!el || !frag) return;

    const caret = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, frag.start);
    const after = el.value.slice(caret);
    const insertion = `@${fullName} `;
    const newValue = before + insertion + after;
    onChange(newValue);
    setMatches([]);
    setDropdownPos(null);

    const newCaret = (before + insertion).length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    });
  }

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    onChange(e.target.value);
    updateMatches(e.target);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape" && matches.length) {
      setMatches([]);
      setDropdownPos(null);
    }
  }

  function handleBlur() {
    // Delayed so a mousedown selection on the dropdown registers first.
    setTimeout(() => {
      setMatches([]);
      setDropdownPos(null);
    }, 150);
  }

  const dropdown =
    matches.length && dropdownPos
      ? createPortal(
          <div
            className="mention-autocomplete"
            style={{ position: "absolute", left: dropdownPos.left, top: dropdownPos.top, minWidth: dropdownPos.minWidth }}
          >
            {matches.map((fullName) => (
              <button
                key={fullName}
                type="button"
                className="mention-autocomplete-item"
                onMouseDown={(e) => {
                  // mousedown, not click, fires before the input's blur.
                  e.preventDefault();
                  applyMention(fullName);
                }}
              >
                {fullName}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  const sharedProps = {
    ref: elRef as never,
    className,
    value,
    placeholder,
    maxLength,
    autoFocus,
    autoComplete: "off" as const,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
  };

  return (
    <>
      {multiline ? <textarea {...sharedProps} rows={rows} /> : <input type="text" {...sharedProps} />}
      {dropdown}
    </>
  );
}
